import { SitemapData, SitemapUrl, ContentGapAnalysis } from '../types/sitemap';
import { EnhancedSearchConsoleData } from '../types';

export class SitemapCrawler {
  private static readonly COMMON_SITEMAP_PATHS = [
    '/sitemap.xml',
    '/sitemap_index.xml',
    '/sitemaps.xml',
    '/sitemap-index.xml',
    '/wp-sitemap.xml',
    '/sitemap1.xml'
  ];

  /**
   * Fetch and parse sitemap for a given domain
   */
  static async fetchSitemap(propertyUrl: string): Promise<SitemapData> {
    const domain = this.extractDomain(propertyUrl);
    
    // Try to find sitemap from robots.txt first
    let sitemapUrls = await this.findSitemapFromRobots(domain);
    
    // If no sitemaps found in robots.txt, try common paths
    if (sitemapUrls.length === 0) {
      sitemapUrls = this.COMMON_SITEMAP_PATHS.map(path => `${domain}${path}`);
    }

    // Try each sitemap URL until one works
    for (const sitemapUrl of sitemapUrls) {
      try {
        const response = await fetch(`/api/sitemap-proxy?url=${encodeURIComponent(sitemapUrl)}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const xmlText = await response.text();
          const urls = await this.parseSitemapXML(xmlText, domain);
          
          if (urls.length > 0) {
            return {
              urls,
              total: urls.length,
              lastFetched: new Date().toISOString(),
              sitemapUrl
            };
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch sitemap from ${sitemapUrl}:`, error);
        continue;
      }
    }

    throw new Error('No accessible sitemap found for this domain');
  }

  /**
   * Parse XML sitemap content
   */
  private static async parseSitemapXML(xmlText: string, domain: string): Promise<SitemapUrl[]> {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    
    // Check for XML parsing errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Invalid XML sitemap');
    }

    // Handle sitemap index files
    const sitemapElements = xmlDoc.querySelectorAll('sitemap > loc');
    if (sitemapElements.length > 0) {
      // This is a sitemap index, fetch individual sitemaps
      const allUrls: SitemapUrl[] = [];
      
      for (const sitemapElement of Array.from(sitemapElements)) {
        const sitemapUrl = sitemapElement.textContent?.trim();
        if (sitemapUrl) {
          try {
            const response = await fetch(`/api/sitemap-proxy?url=${encodeURIComponent(sitemapUrl)}`, {
          credentials: 'include'
        });
            if (response.ok) {
              const subXmlText = await response.text();
              const subUrls = await this.parseSitemapXML(subXmlText, domain);
              allUrls.push(...subUrls);
            }
          } catch (error) {
            console.warn(`Failed to fetch sub-sitemap ${sitemapUrl}:`, error);
          }
        }
      }
      
      return allUrls;
    }

    // Parse regular sitemap URLs
    const urlElements = xmlDoc.querySelectorAll('url');
    const urls: SitemapUrl[] = [];

    for (const urlElement of Array.from(urlElements)) {
      const loc = urlElement.querySelector('loc')?.textContent?.trim();
      if (!loc) continue;

      const lastmod = urlElement.querySelector('lastmod')?.textContent?.trim();
      const changefreq = urlElement.querySelector('changefreq')?.textContent?.trim() as SitemapUrl['changefreq'];
      const priority = urlElement.querySelector('priority')?.textContent?.trim();

      urls.push({
        loc: this.normalizeUrl(loc),
        lastmod,
        changefreq,
        priority: priority ? parseFloat(priority) : undefined
      });
    }

    return urls.filter(url => url.loc.startsWith(domain));
  }

  /**
   * Find sitemap URLs from robots.txt
   */
  private static async findSitemapFromRobots(domain: string): Promise<string[]> {
    try {
      const response = await fetch(`/api/sitemap-proxy?url=${encodeURIComponent(`${domain}/robots.txt`)}`, {
        credentials: 'include'
      });
      if (!response.ok) return [];

      const robotsText = await response.text();
      const sitemapUrls: string[] = [];
      
      const lines = robotsText.split('\n');
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.toLowerCase().startsWith('sitemap:')) {
          const sitemapUrl = trimmedLine.substring(8).trim();
          if (sitemapUrl) {
            sitemapUrls.push(sitemapUrl);
          }
        }
      }

      return sitemapUrls;
    } catch (error) {
      console.warn('Failed to fetch robots.txt:', error);
      return [];
    }
  }

  /**
   * Analyze content gaps between Search Console data and sitemap
   */
  static analyzeContentGaps(
    searchConsoleData: EnhancedSearchConsoleData[],
    sitemapData: SitemapData
  ): ContentGapAnalysis {
    // Get unique URLs from Search Console
    const searchUrls = new Set(
      searchConsoleData
        .filter(item => item.page) // Filter out items without page URLs
        .map(item => this.normalizeUrl(item.page!)) // Use non-null assertion after filter
    );

    // Get sitemap URLs
    const sitemapUrls = sitemapData.urls.map(item => this.normalizeUrl(item.loc));
    const sitemapUrlSet = new Set(sitemapUrls);

    // Find orphaned content (in sitemap but no search traffic)
    const orphanedContent = sitemapData.urls.filter(
      item => !searchUrls.has(this.normalizeUrl(item.loc))
    );

    // Find content opportunities (search queries with no landing page)
    const contentOpportunities = searchConsoleData
      .filter(item => item.page && !sitemapUrlSet.has(this.normalizeUrl(item.page)))
      .map(item => ({
        query: item.query,
        clicks: item.clicks,
        impressions: item.impressions,
        ctr: item.ctr,
        position: item.position
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 50); // Top 50 opportunities

    // Calculate matches
    const matchedUrls = sitemapUrls.filter(url => searchUrls.has(url)).length;
    const coveragePercentage = sitemapData.urls.length > 0 
      ? Math.round((matchedUrls / sitemapData.urls.length) * 100)
      : 0;

    return {
      orphanedContent,
      contentOpportunities,
      totalSitemapUrls: sitemapData.urls.length,
      totalSearchUrls: searchUrls.size,
      matchedUrls,
      summary: {
        orphanedCount: orphanedContent.length,
        opportunityCount: contentOpportunities.length,
        coveragePercentage
      }
    };
  }

  /**
   * Extract domain from property URL
   */
  private static extractDomain(propertyUrl: string): string {
    try {
      const url = new URL(propertyUrl);
      return `${url.protocol}//${url.host}`;
    } catch (error) {
      // If URL parsing fails, assume it's already a domain
      return propertyUrl.startsWith('http') ? propertyUrl : `https://${propertyUrl}`;
    }
  }

  /**
   * Normalize URL for comparison
   */
  private static normalizeUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      // Remove trailing slash, convert to lowercase
      let normalized = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
      if (normalized.endsWith('/') && normalized.length > 1) {
        normalized = normalized.slice(0, -1);
      }
      return normalized.toLowerCase();
    } catch (error) {
      return url.toLowerCase().replace(/\/$/, '');
    }
  }
}

/**
 * Fetch sitemap data for a property
 */
export async function fetchSitemapData(propertyUrl: string): Promise<SitemapData> {
  return SitemapCrawler.fetchSitemap(propertyUrl);
}

/**
 * Analyze content gaps using sitemap data
 */
export function analyzeSitemapContentGaps(
  searchConsoleData: EnhancedSearchConsoleData[],
  sitemapData: SitemapData
): ContentGapAnalysis {
  return SitemapCrawler.analyzeContentGaps(searchConsoleData, sitemapData);
}